'use client';

import { c as _c } from "react/compiler-runtime";
import { Autocomplete } from '@base-ui/react/autocomplete';
import { type ComponentProps, type SubmitEventHandler, useMemo } from 'react';
import { SearchControls, SearchPopup, SearchResults } from './components';
import { useSearchCoordinator, useSearchFlyout, useSearchOverlay } from './hooks';
import { mapSearchResults } from './lib';
import type { SearchResultItem } from './search.types';
import './search.scss';
type SearchInputKeyDownHandler = NonNullable<ComponentProps<typeof Autocomplete.Input>['onKeyDown']>;
const ignoreCommandFailure = () => undefined;
export function Search() {
  const $ = _c(51);
  const {
    clearQuery,
    completeAssistedSearch,
    hasVisibleResults,
    onInputKeyDown,
    onQueryChange,
    query,
    refresh,
    selectSearchResult,
    sources,
    submitSearch
  } = useSearchCoordinator();
  const {
    closeSearch,
    inputRef,
    onInputFocus,
    onOpenChange,
    open
  } = useSearchFlyout();
  const {
    controlsRef,
    formRef,
    isFullscreen,
    portalContainer,
    searchRootRef,
    setSearchRoot
  } = useSearchOverlay(open);
  let t0;
  if ($[0] !== sources) {
    t0 = mapSearchResults(sources);
    $[0] = sources;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const resultSources = t0;
  let t1;
  if ($[2] !== clearQuery || $[3] !== closeSearch) {
    t1 = () => {
      clearQuery();
      closeSearch();
    };
    $[2] = clearQuery;
    $[3] = closeSearch;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleCancel = t1;
  let t2;
  if ($[5] !== onInputFocus || $[6] !== query || $[7] !== refresh) {
    t2 = () => {
      if (onInputFocus()) {
        refresh(query);
      }
    };
    $[5] = onInputFocus;
    $[6] = query;
    $[7] = refresh;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleInputFocus = t2;
  let t3;
  if ($[9] !== completeAssistedSearch || $[10] !== selectSearchResult) {
    t3 = result => {
      const command = result.action === "primary" ? selectSearchResult(result) : completeAssistedSearch(result);
      command.catch(ignoreCommandFailure);
    };
    $[9] = completeAssistedSearch;
    $[10] = selectSearchResult;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleResult = t3;
  let t4;
  if ($[12] !== onInputKeyDown) {
    t4 = event => {
      onInputKeyDown(event);
    };
    $[12] = onInputKeyDown;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleInputKeyDown = t4;
  let t5;
  if ($[14] !== closeSearch || $[15] !== query || $[16] !== submitSearch) {
    t5 = event_0 => {
      event_0.preventDefault();
      if (!query.trim()) {
        return;
      }
      submitSearch().then(destination => {
        if (destination === "search-results") {
          closeSearch();
        }
      }).catch(ignoreCommandFailure);
    };
    $[14] = closeSearch;
    $[15] = query;
    $[16] = submitSearch;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleSubmit = t5;
  const t6 = open && isFullscreen ? "" : undefined;
  const t7 = open ? "" : undefined;
  const t8 = open && isFullscreen ? handleCancel : undefined;
  let t9;
  if ($[18] !== controlsRef || $[19] !== formRef || $[20] !== handleInputFocus || $[21] !== handleInputKeyDown || $[22] !== handleSubmit || $[23] !== inputRef || $[24] !== query || $[25] !== t8) {
    t9 = <SearchControls controlsRef={controlsRef} formRef={formRef} inputRef={inputRef} onCancel={t8} onInputFocus={handleInputFocus} onInputKeyDown={handleInputKeyDown} onSubmit={handleSubmit} query={query} />;
    $[18] = controlsRef;
    $[19] = formRef;
    $[20] = handleInputFocus;
    $[21] = handleInputKeyDown;
    $[22] = handleSubmit;
    $[23] = inputRef;
    $[24] = query;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== handleResult || $[28] !== hasVisibleResults || $[29] !== isFullscreen || $[30] !== open || $[31] !== portalContainer || $[32] !== query || $[33] !== resultSources || $[34] !== searchRootRef) {
    t10 = portalContainer && open && <SearchPopup anchorRef={searchRootRef} isFullscreen={isFullscreen} portalContainer={portalContainer}>{hasVisibleResults && <SearchResults onResult={handleResult} query={query} sources={resultSources} />}</SearchPopup>;
    $[27] = handleResult;
    $[28] = hasVisibleResults;
    $[29] = isFullscreen;
    $[30] = open;
    $[31] = portalContainer;
    $[32] = query;
    $[33] = resultSources;
    $[34] = searchRootRef;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Autocomplete.Status className="header-search__status" />;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== isFullscreen || $[38] !== onOpenChange || $[39] !== onQueryChange || $[40] !== open || $[41] !== query || $[42] !== resultSources || $[43] !== t10 || $[44] !== t9) {
    t12 = <Autocomplete.Root grid={true} items={resultSources} modal={isFullscreen} mode="none" onOpenChange={onOpenChange} onValueChange={onQueryChange} open={open} openOnInputClick={true} value={query}>{t9}{t10}{t11}</Autocomplete.Root>;
    $[37] = isFullscreen;
    $[38] = onOpenChange;
    $[39] = onQueryChange;
    $[40] = open;
    $[41] = query;
    $[42] = resultSources;
    $[43] = t10;
    $[44] = t9;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== setSearchRoot || $[47] !== t12 || $[48] !== t6 || $[49] !== t7) {
    t13 = <div ref={setSearchRoot} className="header-search" data-fullscreen={t6} data-header-overlay-open={t7}>{t12}</div>;
    $[46] = setSearchRoot;
    $[47] = t12;
    $[48] = t6;
    $[49] = t7;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  return t13;
}